/* Admin Catalog API hub.
 *
 * Mirrors every buyer/seller-facing surface we built so support can:
 *   • see who has the API on/off + force-toggle anyone
 *   • see every key on the platform + emergency-revoke single or bulk
 *   • inspect margin rules, webhook subs, Shopify connectors
 *   • view broadcast invite history + admin audit log
 *
 * Single page with tabs.
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import {
  Plug, KeyRound, Activity, Webhook, ShoppingBag, Send, FileText,
  Search, AlertTriangle, RefreshCw, Trash2, Power, Eye, ChevronRight,
  Users, Building2, Loader2, Clock, ShieldAlert,
} from "lucide-react";

const TABS = [
  { id: "overview",   label: "Overview",       icon: Activity },
  { id: "sellers",    label: "Sellers",        icon: Building2 },
  { id: "keys",       label: "All keys",       icon: KeyRound },
  { id: "margin",     label: "Margin rules",   icon: ChevronRight },
  { id: "webhooks",   label: "Webhooks",       icon: Webhook },
  { id: "shopify",    label: "Shopify",        icon: ShoppingBag },
  { id: "broadcasts", label: "Invite log",     icon: Send },
  { id: "audit",      label: "Admin audit",    icon: ShieldAlert },
];

export default function AdminCatalogApi() {
  const [tab, setTab] = useState("overview");
  return (
    <>
      <PageHeader title="Catalog API — admin" subtitle="Force-toggle sellers · revoke keys · audit margin rules, webhooks, Shopify, broadcasts."/>
      <div className="px-8 py-3 border-b border-border bg-white sticky top-0 z-10 overflow-x-auto">
        <div className="inline-flex gap-1 text-xs">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`admin-catapi-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full transition-colors ${tab === t.id ? "bg-foreground text-background" : "text-foreground/65 hover:text-foreground"}`}>
              <t.icon className="w-3.5 h-3.5"/>{t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-8 max-w-7xl mx-auto">
        {tab === "overview"   && <OverviewPanel onJump={setTab}/>}
        {tab === "sellers"    && <SellersPanel/>}
        {tab === "keys"       && <KeysPanel/>}
        {tab === "margin"     && <MarginPanel/>}
        {tab === "webhooks"   && <WebhooksPanel/>}
        {tab === "shopify"    && <ShopifyPanel/>}
        {tab === "broadcasts" && <BroadcastsPanel/>}
        {tab === "audit"      && <AuditPanel/>}
      </div>
    </>
  );
}

/* ─────────────────────── Overview ─────────────────────── */
function OverviewPanel({ onJump }) {
  const [kpi, setKpi] = useState(null);
  useEffect(() => {
    api.get("/admin/catalog-api/overview").then(r => setKpi(r.data));
  }, []);
  if (!kpi) return <Loading/>;
  const tiles = [
    { id: "sellers",    label: "Sellers enabled", value: `${kpi.sellers_enabled} / ${kpi.sellers_total}`, sub: `${kpi.sellers_disabled} disabled` },
    { id: "keys",       label: "Active API keys", value: kpi.keys_active, sub: `${kpi.keys_live} live · ${kpi.keys_test} test` },
    { id: "keys",       label: "New keys / 30d",  value: kpi.new_keys_30d },
    { id: "keys",       label: "Total requests",  value: (kpi.total_requests || 0).toLocaleString() },
    { id: "shopify",    label: "Shopify connectors",  value: kpi.shopify_active },
    { id: "webhooks",   label: "Webhook subs",    value: kpi.webhook_subs },
    { id: "margin",     label: "Margin rule sets", value: kpi.margin_rules },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-catapi-overview">
      {tiles.map((t, i) => (
        <button key={i} onClick={() => onJump(t.id)}
          className="rounded-2xl bg-white border border-border p-5 text-left hover:border-foreground/40 transition-colors">
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55 mb-1">{t.label}</div>
          <div className="display text-2xl">{t.value}</div>
          {t.sub && <div className="text-[10.5px] text-foreground/55 mt-1">{t.sub}</div>}
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────── Sellers ─────────────────────── */
function SellersPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");  // "" | "true" | "false"
  const refresh = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (filter !== "") qs.set("enabled", filter);
    api.get(`/admin/catalog-api/sellers?${qs.toString()}`)
      .then(r => setRows(r.data?.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const toggleSeller = async (row) => {
    const reason = window.prompt(`Force ${row.enabled ? "DISABLE" : "ENABLE"} catalog API for ${row.seller_name || row.seller_id}? Enter a reason (audit-logged):`);
    if (reason === null) return;
    try {
      await api.put(`/admin/catalog-api/sellers/${row.seller_id}/toggle`, { enabled: !row.enabled, reason });
      toast.success(`Force-toggled · ${row.seller_name || row.seller_id}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Toggle failed"); }
  };

  return (
    <div className="space-y-3" data-testid="admin-catapi-sellers">
      <div className="flex gap-2 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && refresh()}
            placeholder="Search seller name / email / id" className="pl-9 h-10" data-testid="sellers-search"/>
        </div>
        <select value={filter} onChange={(e) => { setFilter(e.target.value); setTimeout(refresh, 0); }}
          data-testid="sellers-filter"
          className="h-10 rounded-md border border-input bg-white px-3 text-sm">
          <option value="">All states</option>
          <option value="true">Enabled only</option>
          <option value="false">Disabled only</option>
        </select>
        <Button variant="outline" onClick={refresh} className="rounded-full h-10"><RefreshCw className="w-3.5 h-3.5"/></Button>
      </div>

      {loading ? <Loading/> : (
        <div className="rounded-xl border border-border bg-white overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-secondary/40 text-[10px] uppercase tracking-wider text-foreground/55">
              <tr>
                <Th>Seller</Th>
                <Th>State</Th>
                <Th>Buyers</Th>
                <Th>Active keys</Th>
                <Th>Requests</Th>
                <Th>Last used</Th>
                <Th>First activated</Th>
                <Th right>Action</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.seller_id} className="border-t border-border" data-testid={`seller-row-${r.seller_id}`}>
                  <Td>
                    <div className="font-medium">{r.seller_name || "—"}</div>
                    <div className="text-[10.5px] text-foreground/55">{r.seller_email}</div>
                  </Td>
                  <Td>
                    {r.enabled
                      ? <span className="chip bg-emerald-50 border-emerald-200 text-emerald-700 text-[10px]">enabled</span>
                      : <span className="chip bg-rose-50 border-rose-200 text-rose-700 text-[10px]">disabled</span>}
                    {r.grandfathered && <span className="ml-1 chip bg-secondary text-[9.5px]">grandfathered</span>}
                  </Td>
                  <Td><span className="mono">{r.buyers_count}</span></Td>
                  <Td><span className="mono">{r.active_keys}</span></Td>
                  <Td><span className="mono">{(r.total_requests || 0).toLocaleString()}</span></Td>
                  <Td><Time t={r.last_used_at}/></Td>
                  <Td><Time t={r.first_activated_at}/></Td>
                  <Td right>
                    <Switch checked={!!r.enabled} onCheckedChange={() => toggleSeller(r)}
                      data-testid={`seller-toggle-${r.seller_id}`}
                      className="data-[state=checked]:bg-emerald-600 scale-90"/>
                  </Td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="text-center text-foreground/40 py-10">No sellers</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── Keys ─────────────────────── */
function KeysPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [env, setEnv] = useState("");
  const [state, setState] = useState("active");
  const [revokeOpen, setRevokeOpen] = useState(false);
  const refresh = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (env) qs.set("env", env);
    if (state) qs.set("state", state);
    api.get(`/admin/catalog-api/keys?${qs.toString()}`)
      .then(r => setRows(r.data?.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const revokeOne = async (k) => {
    const reason = window.prompt(`Emergency revoke key "${k.name}" (${k.key_prefix}…${k.last_four})? Enter audit reason:`);
    if (reason === null) return;
    try {
      const r = await api.post("/admin/catalog-api/keys/revoke", { key_id: k.id, reason });
      toast.success(`Revoked ${r.data.revoked} key`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); }
  };

  return (
    <div className="space-y-3" data-testid="admin-catapi-keys">
      <div className="flex gap-2 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && refresh()}
            placeholder="Search seller / buyer / key name" className="pl-9 h-10" data-testid="keys-search"/>
        </div>
        <select value={env} onChange={(e) => { setEnv(e.target.value); setTimeout(refresh, 0); }}
          data-testid="keys-env-filter"
          className="h-10 rounded-md border border-input bg-white px-3 text-sm">
          <option value="">All envs</option>
          <option value="live">Live only</option>
          <option value="test">Test (sandbox) only</option>
        </select>
        <select value={state} onChange={(e) => { setState(e.target.value); setTimeout(refresh, 0); }}
          data-testid="keys-state-filter"
          className="h-10 rounded-md border border-input bg-white px-3 text-sm">
          <option value="active">Active</option>
          <option value="revoked">Revoked</option>
          <option value="all">All</option>
        </select>
        <Button variant="outline" onClick={refresh} className="rounded-full h-10"><RefreshCw className="w-3.5 h-3.5"/></Button>
        <Button onClick={() => setRevokeOpen(true)}
          data-testid="bulk-revoke-btn"
          className="rounded-full h-10 bg-rose-600 hover:bg-rose-700 text-white">
          <Trash2 className="w-3.5 h-3.5 mr-1.5"/>Bulk revoke
        </Button>
      </div>

      {loading ? <Loading/> : (
        <div className="rounded-xl border border-border bg-white overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-secondary/40 text-[10px] uppercase tracking-wider text-foreground/55">
              <tr>
                <Th>Key</Th>
                <Th>Env</Th>
                <Th>Seller</Th>
                <Th>Buyer</Th>
                <Th>Requests</Th>
                <Th>Last used</Th>
                <Th>State</Th>
                <Th right>Action</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(k => (
                <tr key={k.id} className="border-t border-border" data-testid={`key-row-${k.id}`}>
                  <Td>
                    <div className="font-medium">{k.name}</div>
                    <div className="mono text-[10px] text-foreground/55">{k.key_prefix}…{k.last_four}</div>
                  </Td>
                  <Td><span className={`chip text-[10px] ${k.env === "test" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-secondary"}`}>{k.env}</span></Td>
                  <Td>
                    <div>{k.seller_name || "—"}</div>
                    <div className="text-[10px] text-foreground/55">{k.seller_email}</div>
                  </Td>
                  <Td>
                    <div>{k.buyer_name || "—"}</div>
                    <div className="text-[10px] text-foreground/55">{k.buyer_email}</div>
                  </Td>
                  <Td><span className="mono">{(k.request_count || 0).toLocaleString()}</span></Td>
                  <Td><Time t={k.last_used_at}/></Td>
                  <Td>{k.revoked_at
                    ? <span className="chip bg-rose-50 border-rose-200 text-rose-700 text-[10px]">revoked</span>
                    : <span className="chip bg-emerald-50 border-emerald-200 text-emerald-700 text-[10px]">active</span>}</Td>
                  <Td right>
                    {!k.revoked_at && (
                      <button onClick={() => revokeOne(k)} data-testid={`revoke-key-${k.id}`}
                        className="text-rose-600 hover:text-rose-800 inline-flex items-center gap-1 text-[11px]">
                        <Trash2 className="w-3 h-3"/>Revoke
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="text-center text-foreground/40 py-10">No keys</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <BulkRevokeModal open={revokeOpen} onClose={() => setRevokeOpen(false)} onDone={refresh}/>
    </div>
  );
}

function BulkRevokeModal({ open, onClose, onDone }) {
  const [buyerId, setBuyerId] = useState("");
  const [sellerId, setSellerId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) { setBuyerId(""); setSellerId(""); setReason(""); } }, [open]);
  const run = async () => {
    if (!buyerId && !sellerId) { toast.error("Provide buyer_id or seller_id"); return; }
    if (!reason.trim()) { toast.error("Reason required for audit"); return; }
    if (!window.confirm(`Revoke ALL active keys matching ${buyerId ? "buyer="+buyerId : ""} ${sellerId ? "seller="+sellerId : ""}? Cannot undo.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/catalog-api/keys/revoke", { buyer_id: buyerId || null, seller_id: sellerId || null, reason });
      toast.success(`Revoked ${r.data.revoked} key(s)`);
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="bulk-revoke-modal">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-rose-600"/>Bulk revoke keys</DialogTitle>
          <DialogDescription>Provide at least one identifier. Action is audit-logged and cannot be undone.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Input value={buyerId}  onChange={(e) => setBuyerId(e.target.value)}  placeholder="buyer_id (optional)"  className="mono text-[11.5px]"/>
          <Input value={sellerId} onChange={(e) => setSellerId(e.target.value)} placeholder="seller_id (optional)" className="mono text-[11.5px]"/>
          <Input value={reason}   onChange={(e) => setReason(e.target.value)}   placeholder="Audit reason (required)"/>
          <Button onClick={run} disabled={busy} data-testid="bulk-revoke-confirm"
            className="w-full rounded-full bg-rose-600 hover:bg-rose-700 text-white h-10">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <Trash2 className="w-3.5 h-3.5 mr-1.5"/>}
            Revoke matching keys
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────── Margin / Webhooks / Shopify / Broadcasts / Audit ─────────────────────── */
function GenericListPanel({ endpoint, columns, emptyText, testid }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = () => {
    setLoading(true);
    api.get(endpoint).then(r => setRows(r.data?.data || [])).finally(() => setLoading(false));
  };
  useEffect(refresh, [endpoint]);
  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex justify-between items-center">
        <div className="text-[11px] text-foreground/55">{rows.length} row{rows.length === 1 ? "" : "s"}</div>
        <Button variant="outline" onClick={refresh} className="rounded-full h-9 text-xs">
          <RefreshCw className="w-3.5 h-3.5 mr-1"/>Refresh
        </Button>
      </div>
      {loading ? <Loading/> : rows.length === 0 ? (
        <div className="text-center text-foreground/40 py-10 text-[12px]">{emptyText}</div>
      ) : (
        <div className="rounded-xl border border-border bg-white overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-secondary/40 text-[10px] uppercase tracking-wider text-foreground/55">
              <tr>{columns.map((c, i) => <Th key={i} right={c.right}>{c.label}</Th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-border">
                  {columns.map((c, j) => <Td key={j} right={c.right}>{c.render ? c.render(r) : r[c.field]}</Td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const MarginPanel = () => <GenericListPanel
  endpoint="/admin/catalog-api/margin-rules"
  emptyText="No margin rules configured"
  testid="admin-catapi-margin"
  columns={[
    { label: "Buyer",      render: r => <><div>{r.buyer_name}</div><div className="mono text-[10px] text-foreground/55">{r.buyer_id?.slice(0,8)}</div></> },
    { label: "Seller",     render: r => <><div>{r.seller_name}</div><div className="mono text-[10px] text-foreground/55">{r.seller_id?.slice(0,8)}</div></> },
    { label: "Rules",      render: r => <span className="mono">{r.rule_count}</span> },
    { label: "Round to",   render: r => <span className="mono">{r.round_to || "—"}</span> },
    { label: "Updated",    render: r => <Time t={r.updated_at}/> },
  ]}
/>;

const WebhooksPanel = () => <GenericListPanel
  endpoint="/admin/catalog-api/webhooks"
  emptyText="No webhook subscriptions"
  testid="admin-catapi-webhooks"
  columns={[
    { label: "Buyer",  render: r => r.buyer_name || r.buyer_id?.slice(0,8) },
    { label: "Seller", render: r => r.seller_name || r.seller_id?.slice(0,8) },
    { label: "URL",    render: r => <span className="mono text-[10.5px] break-all">{r.url}</span> },
    { label: "State",  render: r => r.active
                       ? <span className="chip bg-emerald-50 border-emerald-200 text-emerald-700 text-[10px]">active</span>
                       : <span className="chip bg-secondary text-[10px]">paused</span> },
    { label: "Created", render: r => <Time t={r.created_at}/> },
  ]}
/>;

function ShopifyPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = () => {
    setLoading(true);
    api.get("/admin/catalog-api/shopify").then(r => setRows(r.data?.data || [])).finally(() => setLoading(false));
  };
  useEffect(refresh, []);
  const sync = async (cid) => {
    try {
      const r = await api.post(`/admin/catalog-api/shopify/${cid}/sync`);
      toast.success(`Synced ${r.data?.synced || 0} products${r.data?.mock ? " (mock)" : ""}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail?.message || "Sync failed"); }
  };
  return (
    <div className="space-y-3" data-testid="admin-catapi-shopify">
      <div className="flex justify-between"><div className="text-[11px] text-foreground/55">{rows.length} connector{rows.length === 1 ? "" : "s"}</div>
        <Button variant="outline" onClick={refresh} className="rounded-full h-9 text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1"/>Refresh</Button></div>
      {loading ? <Loading/> : rows.length === 0 ? (
        <div className="text-center text-foreground/40 py-10 text-[12px]">No Shopify connectors</div>
      ) : (
        <div className="rounded-xl border border-border bg-white overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-secondary/40 text-[10px] uppercase tracking-wider text-foreground/55">
              <tr><Th>Buyer</Th><Th>Seller</Th><Th>Shop</Th><Th>Status</Th><Th>Last sync</Th><Th right>Action</Th></tr>
            </thead>
            <tbody>
              {rows.map((c, i) => (
                <tr key={i} className="border-t border-border">
                  <Td>{c.buyer_name || c.buyer_id?.slice(0,8)}</Td>
                  <Td>{c.seller_name || c.seller_id?.slice(0,8)}</Td>
                  <Td><span className="mono text-[10.5px]">{c.config?.shop_domain}</span>{c.config?.mock && <span className="ml-1 chip bg-amber-50 border-amber-200 text-amber-800 text-[9.5px]">mock</span>}</Td>
                  <Td>{c.enabled
                    ? <span className="chip bg-emerald-50 border-emerald-200 text-emerald-700 text-[10px]">{c.last_status || "ready"}</span>
                    : <span className="chip bg-secondary text-[10px]">disabled</span>}</Td>
                  <Td><div><Time t={c.last_sync_at}/></div>{c.last_sync_count != null && <div className="text-[10px] text-foreground/55">{c.last_sync_count} products</div>}</Td>
                  <Td right>
                    <button onClick={() => sync(c.id)} className="text-foreground/65 hover:text-foreground inline-flex items-center gap-1 text-[11px]" data-testid={`admin-shopify-sync-${c.id}`}>
                      <RefreshCw className="w-3 h-3"/>Sync now
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const BroadcastsPanel = () => <GenericListPanel
  endpoint="/admin/catalog-api/broadcast-log"
  emptyText="No broadcast invites yet"
  testid="admin-catapi-broadcasts"
  columns={[
    { label: "Seller",       render: r => r.seller_name || r.seller_id?.slice(0,8) },
    { label: "Buyers",       render: r => <span className="mono">{(r.buyer_ids || []).length}</span> },
    { label: "Sent",         render: r => <span className="mono text-emerald-700">{r.sent_count || 0}</span> },
    { label: "Failed",       render: r => <span className="mono text-rose-700">{r.failed_count || 0}</span> },
    { label: "Note",         render: r => <span className="text-[10.5px] text-foreground/65 italic">{r.note ? `“${r.note.slice(0, 60)}…”` : "—"}</span> },
    { label: "At",           render: r => <Time t={r.at}/> },
  ]}
/>;

const AuditPanel = () => <GenericListPanel
  endpoint="/admin/catalog-api/audit"
  emptyText="No admin actions logged yet"
  testid="admin-catapi-audit"
  columns={[
    { label: "Action",       render: r => <span className="chip bg-secondary text-[10px]">{r.action}</span> },
    { label: "Admin",        render: r => r.admin_email },
    { label: "Target",       render: r => <span className="mono text-[10px]">{r.seller_id || JSON.stringify(r.filter || {}).slice(0, 60)}</span> },
    { label: "Detail",       render: r => r.action === "force_toggle"
                                ? <span className="text-[10.5px]">{String(r.from)} → <strong>{String(r.to)}</strong></span>
                                : <span className="mono text-[10.5px]">{r.revoked || 0} revoked</span> },
    { label: "Reason",       render: r => <span className="text-[10.5px] text-foreground/65 italic">{r.reason || "—"}</span> },
    { label: "At",           render: r => <Time t={r.at}/> },
  ]}
/>;

/* ─────────────────────── Tiny shared atoms ─────────────────────── */
function Th({ children, right }) {
  return <th className={`px-3 py-2 font-medium ${right ? "text-right" : "text-left"}`}>{children}</th>;
}
function Td({ children, right }) {
  return <td className={`px-3 py-2.5 align-middle ${right ? "text-right" : ""}`}>{children}</td>;
}
function Time({ t }) {
  if (!t) return <span className="text-foreground/40">—</span>;
  try { return <span className="text-[10.5px] mono text-foreground/65">{new Date(t).toLocaleString()}</span>; }
  catch { return <span className="text-foreground/40">—</span>; }
}
function Loading() {
  return <div className="py-12 text-center text-foreground/40 text-[12px]"><Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Loading…</div>;
}
